import '../users-balances.scss';
import * as React from 'react';
import { UserLink, CopyablePhone } from 'components';
import { useNavigate, useParams } from 'react-router-dom';
import { Empty, Input as AntdInput, Table as AntdTable, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, ExportOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { Button, buttonType } from 'components/button/button';
import { Card } from 'components/card/card';
import { Page } from 'components/page/page';
import { UserBalancePerMarket } from 'core/domain/wallet/wallet';
import { getUsersBalanceBySymbol } from 'core/repositories/wallet';
import { digitSperator } from 'core/utils/decimalExtentions';
import { downloadCSV } from 'core/utils/export-to-file';
import { getIconPath } from 'core/utils/getIconPath';

const formatQuantity = (value: number): string => {
    if (value === 0) return '0';
    if (Math.abs(value) < 1) return value.toFixed(8).replace(/0+$/, '').replace(/\.$/, '');
    return digitSperator(Number(value.toFixed(4)));
};

export const UsersBalanceBySymbol = () => {
    const { symbol } = useParams<{ symbol: string }>();
    const [rows, setRows] = React.useState<UserBalancePerMarket[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const navigate = useNavigate();

    const loadData = React.useCallback(() => {
        if (!symbol) return;
        getUsersBalanceBySymbol(setLoading, setRows, symbol);
    }, [symbol]);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const filteredRows = React.useMemo(() => {
        const keyword = search.trim().toLowerCase();
        if (!keyword) return rows;
        return rows.filter(
            (r) =>
                r.user?.fullName?.toLowerCase().includes(keyword) ||
                r.user?.userName?.toLowerCase().includes(keyword),
        );
    }, [rows, search]);

    const totalAvailable = React.useMemo(
        () => filteredRows.reduce((sum, r) => sum + (r.available ?? 0), 0),
        [filteredRows],
    );

    const columns = React.useMemo<ColumnsType<UserBalancePerMarket>>(
        () => [
            {
                title: '#',
                key: 'index',
                width: 70,
                render: (_, __, index) => index + 1,
            },
            {
                title: 'کاربر',
                key: 'user',
                width: 220,
                render: (_, row) => (
                    <div className="customer-cell">
                        <UserLink userId={row.user?.id}>
                            <span>{row.user?.fullName?.trim() || 'بدون نام'}</span>
                        </UserLink>
                        <small>
                            <CopyablePhone phoneNumber={row.user?.userName} />
                        </small>
                    </div>
                ),
            },
            {
                title: 'موجودی',
                dataIndex: 'available',
                key: 'available',
                align: 'left',
                width: 200,
                defaultSortOrder: 'descend',
                sorter: (a, b) => a.available - b.available,
                render: (available: number) => <span className="en">{formatQuantity(available)}</span>,
            },
            {
                title: 'پروفایل',
                key: 'actions',
                width: 100,
                render: (_, row) =>
                    row.user?.id ? (
                        <Tooltip title="مشاهده کاربر">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="مشاهده کاربر"
                                onClick={() => navigate(`/dashboard/users/edit/${row.user.id}`)}
                            >
                                <UserOutlined />
                            </button>
                        </Tooltip>
                    ) : null,
            },
        ],
        [navigate],
    );

    return (
        <Page className="pmx-users-balances" title={`موجودی کاربران - ${symbol ?? ''}`}>
            {/* اسپینر فقط روی خود جدول است؛ blur روی Card باعث دو اسپینر هم‌زمان می‌شد. */}
            <Card title={`لیست کاربران دارای موجودی ${symbol ?? ''}`}>
                <div className="table-toolbar">
                    <div className="symbol-heading">
                        {symbol && <img src={getIconPath(symbol.toLowerCase())} alt="" />}
                        <span className="en">{symbol}</span>
                    </div>
                    <AntdInput.Search
                        className="symbol-search"
                        placeholder="جستجوی نام یا شماره موبایل"
                        allowClear
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                    />
                    <div className="toolbar-actions">
                        <Button
                            className="reload-btn"
                            text="بازگشت"
                            type={buttonType.outlined}
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/dashboard/users-balances')}
                        />
                        <Button
                            className="reload-btn"
                            text="بارگذاری مجدد"
                            type={buttonType.outlined}
                            icon={<ReloadOutlined />}
                            onClick={loadData}
                        />
                        <Button
                            className="export-btn"
                            text="خروجی اکسل"
                            type={buttonType.info}
                            icon={<ExportOutlined />}
                            disabled={!filteredRows.length}
                            onClick={() =>
                                downloadCSV(
                                    `موجودی کاربران - ${symbol}`,
                                    filteredRows.map((r) => ({
                                        fullName: r.user?.fullName,
                                        userName: r.user?.userName,
                                        available: r.available,
                                    })),
                                )
                            }
                        />
                    </div>
                </div>
                <div className="summary-bar">
                    <span>تعداد کاربر: {digitSperator(filteredRows.length)}</span>
                    <span className="en">
                        مجموع موجودی: {formatQuantity(totalAvailable)} {symbol}
                    </span>
                </div>
                <AntdTable<UserBalancePerMarket>
                    className="users-balances-table"
                    rowKey={(row) => row.user?.id || `${row.user?.userName}-${row.available}`}
                    columns={columns}
                    dataSource={filteredRows}
                    loading={loading}
                    locale={{ emptyText: <Empty description="رکوردی یافت نشد" /> }}
                    scroll={{ x: 800 }}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: [20, 50, 100, 1000, 10000, 100000],
                        onChange: (nextPage, nextPageSize) => {
                            setPage(nextPage);
                            setPageSize(nextPageSize);
                        },
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                />
            </Card>
        </Page>
    );
};
